import { Box, BoxProps, Stack } from "@mui/material";
import { chain } from "lodash-es";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { VehicleVisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useValueMemoWithDeepCompare } from "@/common/hooks/memo/useValueMemoWithDeepCompare";
import { VisualModelImageSelector, VisualModelPoint } from "@/common/ts/visualModel";
import { apiClient } from "@/core/api/ApiClient";
import {
  VehicleVisualModelDto,
  VehicleVisualModelImageDto,
  VehicleVisualModelsApiApiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGetRequest,
} from "@/core/api/generated/v1";

import { VisualModelSvgViewer, VisualModelSvgViewerProps } from "./VisualModelSvgViewer";

export interface VisualModelViewerProps<TPoint extends VisualModelPoint = VisualModelPoint> {
  variant?: "default" | "compact";
  direction?: "row" | "column";
  visualModelId: Nil<string>;
  visualModel: Nil<VehicleVisualModelDto>;
  points: TPoint[];
  visualModelLatestOrDefaultFor?: VehicleVisualModelsApiApiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGetRequest;
  /** Initial main image selector. */
  initialMainImageSelector?: VisualModelImageSelector;
  isFetchVisualModel?: boolean;
  isSwitchImageEnabled?: boolean;
  debugKey?: string;
  slots?: {
    topRow?: {
      item1?: ReactNode;
      item2?: ReactNode;
    };
    addDamageControls?: ReactNode;
  };
  mainViewerProps?: Partial<
    Omit<
      VisualModelSvgViewerProps<TPoint>,
      "visualModelId" | "visualModel" | "imageSelector" | "points"
    >
  >;
  secondaryViewerProps?: Partial<
    Omit<
      VisualModelSvgViewerProps<TPoint>,
      "visualModelId" | "visualModel" | "imageSelector" | "points"
    >
  >;
  sx?: BoxProps["sx"];
}

/** Viewer/editor for all visual model SVG images. */
export function VisualModelViewer<TPoint extends VisualModelPoint>({
  variant = "default",
  direction = "row",
  visualModelId,
  visualModel,
  points,
  visualModelLatestOrDefaultFor,
  initialMainImageSelector,
  isFetchVisualModel = true,
  isSwitchImageEnabled = true,
  debugKey,
  slots,
  mainViewerProps,
  secondaryViewerProps,
  sx,
}: VisualModelViewerProps<TPoint>) {
  const isMainImageInitedRef = useRef(false);

  const [selectedImage, setSelectedImage] = useState<Nil<VehicleVisualModelImageDto>>(null);

  const visualModelLatestOrDefaultForMemoized = useValueMemoWithDeepCompare(
    visualModelLatestOrDefaultFor,
  );
  const initialMainImageSelectorMemoized = useValueMemoWithDeepCompare(initialMainImageSelector);

  // fetch by id or fallback to latest or default
  const visualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: visualModelId!,
    },
    {
      deps: [visualModelId],
      skip: !isFetchVisualModel || !visualModelId || !!visualModel,
    },
  );
  const visualModelById = visualModelRequest.data || visualModel;
  const isFallbackToLatestOrDefault =
    (!visualModelId && !visualModel) ||
    (!!visualModelId && visualModelRequest.isFirstEnded && !visualModelRequest.data);

  const visualModelLatestOrDefaultRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGet,
    {
      ...visualModelLatestOrDefaultForMemoized,
    },
    {
      deps: [visualModelLatestOrDefaultForMemoized],
      skip:
        !isFetchVisualModel ||
        !isFallbackToLatestOrDefault ||
        !visualModelLatestOrDefaultForMemoized,
    },
  );
  const visualModelLatestOrDefault = visualModelLatestOrDefaultRequest.data;

  const visualModelComputed = visualModel || visualModelById || visualModelLatestOrDefault;
  const isIniting =
    !visualModelComputed ||
    (visualModelRequest.isLoading && !visualModelRequest.isFirstEnded) ||
    (visualModelLatestOrDefaultRequest.isLoading &&
      !visualModelLatestOrDefaultRequest.isFirstEnded);

  const currentPoints = useMemo(() => points.filter((x) => x.meta.type === "current"), [points]);
  // const oldPoints = useMemo(() => points.filter((x) => x.meta.type === "old"), [points]);

  // map points to images
  const pointsByImageIdMap = useMemo<Record<string, TPoint[]>>(() => {
    if (!visualModelComputed) {
      return {};
    }
    const pairs = visualModelComputed?.images?.map((image) => ({
      image,
      points: points.filter(
        (point) => point.meta.area === image.area && point.meta.projection == image.projection,
      ),
    }));
    return chain(pairs)
      .keyBy((x) => x.image.id || "")
      .mapValues((x) => x.points)
      .value() as Record<string, TPoint[]>;
  }, [visualModelComputed, points]);
  const currentPointsByImageIdMap = useMemo<Record<string, TPoint[]>>(() => {
    if (!visualModelComputed) {
      return {};
    }
    const pairs = visualModelComputed?.images?.map((image) => ({
      image,
      points: points.filter(
        (point) =>
          point.meta.area === image.area &&
          point.meta.projection == image.projection &&
          point.meta.type !== "old",
      ),
    }));
    return chain(pairs)
      .keyBy((x) => x.image.id || "")
      .mapValues((x) => x.points)
      .value() as Record<string, TPoint[]>;
  }, [visualModelComputed, points]);

  // auto-select main image on init or when visual model changes
  useEffect(() => {
    const newImage = VehicleVisualModelHelper.getImageBySelectorOrFirst(
      visualModelComputed,
      initialMainImageSelectorMemoized,
      points,
    );
    setSelectedImage(newImage || null);
  }, [visualModelComputed]);
  useEffect(() => {
    if (isMainImageInitedRef.current) {
      return;
    }
    const newImage = VehicleVisualModelHelper.getImageBySelectorOrFirst(
      visualModelComputed,
      initialMainImageSelectorMemoized,
      currentPoints,
    );
    setSelectedImage(newImage || null);
    isMainImageInitedRef.current = true;
  }, [initialMainImageSelectorMemoized, currentPoints]);

  return (
    <Box
      sx={{
        display: "grid",
        columnGap: 2,
        rowGap: 1,
        ...(direction === "row"
          ? { gridTemplateColumns: { xxs: "1fr", md: "auto 1fr" }, justifyItems: "stretch" }
          : {}),
        ...(direction === "column" ? { gridTemplateColumns: "1fr", justifyItems: "start" } : {}),
        ...sx,
      }}
    >
      {/* Slots */}
      {slots?.topRow && (
        <>
          {slots.topRow.item1}
          {slots.topRow.item2}
        </>
      )}

      {/* Primary cell: Main image */}
      <VisualModelSvgViewer
        visualModelId={undefined}
        visualModel={visualModelComputed}
        imageSelector={{
          image: selectedImage,
        }}
        points={pointsByImageIdMap[selectedImage?.id || ""] || []}
        withVisualModelInfo
        isFetchVisualModel={false}
        debugKey={debugKey}
        svgViewerProps={{
          isLoading: isIniting,
          isZoomEnabled: true,
          isResizeEnabled: true,
          isPointDragEnabled: false,
          ...mainViewerProps?.svgViewerProps,
          svgProps: {
            isDisableHoverEffects: false,
            ...mainViewerProps?.svgViewerProps?.svgProps,
          },
          defaultPointStyle: {
            ...VehicleVisualModelHelper.defaultSvgPointStyleForDamage,
            ...mainViewerProps?.svgViewerProps?.defaultPointStyle,
          },
        }}
        {...mainViewerProps}
      />

      {/* Secondary cell */}
      {!isIniting && visualModelComputed && (
        <Stack
          direction='column'
          sx={{
            ...(variant === "default" ? { alignSelf: { xxs: "stretch", lg: "center" } } : {}),
            ...(variant === "compact" ? { alignSelf: "stretch" } : {}),
            alignItems: { xxs: "stretch", md: "stretch" },
            gap: 2,
          }}
        >
          {/* Secondary images (main image selector) */}
          <Stack
            direction='row'
            sx={{
              flexWrap: "wrap",
              alignItems: { xxs: "flex-start", md: "center" },
              gap: 1,
            }}
          >
            {visualModelComputed.images?.map((image, index) => {
              const points2 = pointsByImageIdMap[image.id || ""] || [];
              const currentPoints2 = currentPointsByImageIdMap[image.id || ""] || [];

              return (
                <Box
                  key={index}
                  sx={{ position: "relative" }}
                  onClick={() => {
                    if (isSwitchImageEnabled) {
                      setSelectedImage(image);
                    }
                  }}
                >
                  <VisualModelSvgViewer
                    visualModelId={undefined}
                    visualModel={visualModelComputed}
                    imageSelector={{
                      image: image,
                    }}
                    points={points2}
                    isFetchVisualModel={false}
                    withVisualModelInfo={false}
                    svgViewerProps={{
                      svgContainerSx: {
                        backgroundColor: (theme) => theme.palette.background.gray,
                        borderWidth: 2,
                        borderStyle: "solid",
                        borderColor: (theme) =>
                          image.id === selectedImage?.id
                            ? theme.palette.secondary.main
                            : "transparent",
                        borderRadius: (theme) => theme.shapeCustom.borderRadiusCard,
                        cursor: isSwitchImageEnabled ? "pointer" : "default",
                      },
                      svgProps: {
                        ...(variant === "default"
                          ? {
                              width: { xxs: 100, md: 150, lg: 150 },
                              height: { xxs: 100, md: 150, lg: 150 },
                              minWidth: { xxs: 100, md: 150, lg: 150 },
                              minHeight: { xxs: 100, md: 150, lg: 150 },
                              maxWidth: { xxs: 100, md: 150, lg: 150 },
                              maxHeight: { xxs: 100, md: 150, lg: 150 },
                            }
                          : {}),
                        ...(variant === "compact"
                          ? {
                              width: { xxs: 50, sm: 60, md: 80, lg: 100 },
                              height: { xxs: 50, sm: 60, md: 80, lg: 100 },
                              minWidth: { xxs: 50, sm: 60, md: 80, lg: 100 },
                              minHeight: { xxs: 50, sm: 60, md: 80, lg: 100 },
                              maxWidth: { xxs: 50, sm: 60, md: 80, lg: 100 },
                              maxHeight: { xxs: 50, sm: 60, md: 80, lg: 100 },
                            }
                          : {}),
                        cursor: isSwitchImageEnabled ? "pointer" : "default",
                        isDisableHoverEffects: true,
                        ...secondaryViewerProps?.svgViewerProps?.svgProps,
                      },
                      isZoomEnabled: false,
                      isResizeEnabled: false,
                      isPointDragEnabled: false,
                      ...secondaryViewerProps?.svgViewerProps,
                      defaultPointStyle: {
                        ...VehicleVisualModelHelper.defaultSvgPointStyleForDamage,
                        circleRadius: 6, // ensure points are distinguishable
                        ...secondaryViewerProps?.svgViewerProps?.defaultPointStyle,
                      },
                    }}
                  />

                  {/* Check icon if has current points */}
                  {TypeHelper.isNotEmptyArray(currentPoints2) && (
                    <AppIcon
                      sx={{
                        position: "absolute",
                        zIndex: 1,
                        top: 5,
                        right: 5,
                        color: "error.main",
                      }}
                      of='successFilled'
                      fontSize='medium'
                    />
                  )}
                </Box>
              );
            })}
          </Stack>

          {slots?.addDamageControls}
        </Stack>
      )}
    </Box>
  );
}
